/**
 * Named DIME cloud environments → web-api base URLs.
 *
 * Hosts normally pick an environment by name; `apiUrl` in
 * `DimeCloudOptions`/`DimeCloudTerminal` always wins over `environment`, so a
 * new/unlisted backend can be targeted without a package release.
 */

export type DimeEnvironment = 'production' | 'testnet';

export const DIME_API_URLS: Readonly<Record<DimeEnvironment, string>> = {
  production: 'https://api.dimeterminal.ai',
  testnet: 'https://api.testnet.dimeterminal.ai',
};

/**
 * Clerk PUBLISHABLE keys per environment — public by design (a pk_* key is
 * just the base64-encoded frontend API domain; the sk_* secret counterpart
 * never leaves Clerk's dashboard/servers). Bundled so hosts using
 * `DimeCloudApp` need only name an environment; each key's Clerk instance is
 * the issuer that environment's web-api trusts (IDENTITY_ISSUER in
 * infra/helm/terminal/environments/*).
 */
export const DIME_CLERK_PUBLISHABLE_KEYS: Readonly<
  Record<DimeEnvironment, string>
> = {
  production: 'pk_live_Y2xlcmsuZGltZXRlcm1pbmFsLmFpJA',
  testnet: 'pk_test_d2hvbGUtZ3JpZmZvbi05OC5jbGVyay5hY2NvdW50cy5kZXYk',
};

/**
 * Resolve the Clerk publishable key: explicit override wins, then the
 * environment's bundled key, then production. Note: an `apiUrl` override
 * does NOT influence the key — a host targeting a custom backend must pass
 * the matching key explicitly.
 */
export function resolveClerkPublishableKey(opts: {
  readonly environment?: DimeEnvironment;
  readonly publishableKey?: string;
}): string {
  return (
    opts.publishableKey ??
    DIME_CLERK_PUBLISHABLE_KEYS[opts.environment ?? 'production'] ??
    DIME_CLERK_PUBLISHABLE_KEYS.production
  );
}

/** Resolve the web-api base URL: explicit `apiUrl` wins, then `environment`, then production. */
export function resolveDimeApiUrl(opts: {
  readonly environment?: DimeEnvironment;
  readonly apiUrl?: string;
}): string {
  const raw =
    opts.apiUrl ??
    DIME_API_URLS[opts.environment ?? 'production'] ??
    DIME_API_URLS.production;
  return raw.replace(/\/+$/u, '');
}
